/* eslint-disable no-console */
import {
  DownloadOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  UploadOutlined
} from '@ant-design/icons';
import {Button, Flex, message, Modal, Tooltip, Upload} from 'antd';
import type {UploadProps} from 'antd';
import React, {useEffect, useRef, useState} from 'react';

import {ReactComponent as JobIcon} from '../../../assets/icons/jobsIcon.svg';
import UpgradeButton from '../../../components/UpgradeButton/UpgradeButton';
import {
  getUserCreationUploadConfirmMessage,
  USER_CREATION_UPLOAD_CONFIRM_TITLE
} from '../../../constants/uploadLimits';
import {allUploadType} from '../../../services/constants/uploadTypes';
import {uploadFile} from '../../../services/modules/UploadAndJobs/uploadFile';
// import {accessModule} from '../../../store/companyStore/companyDetailsStore';
import {
  isOnboardingDone,
  shouldBlockReadOnly,
  shouldBlockFreeOrReadOnly
} from '../../../utils/SubscriptionHelper';
// import DownloadSampleModal from '../../DownloadTemplate/DownloadSampleModel';

import TableBulkSelectionBar from './TableBulkSelectionBar';

const SPREADSHEET_UPLOAD_ACCEPT =
  '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const validateSpreadsheetFile: UploadProps['beforeUpload'] = (file) => {
  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    void message.error('Invalid file type. Please upload an XLSX file.');

    return Upload.LIST_IGNORE;
  }

  return true;
};

const TableTitleHeader: React.FC<{
  title?: string;
  filter: {facilityId: string; selectedYear: string};
  addButton: {
    setShowModel: React.Dispatch<React.SetStateAction<boolean>> | undefined;
    setIsEdit: React.Dispatch<React.SetStateAction<boolean>> | undefined;
    buttonTitle: string;
  };
  sampleDownload: {downTitle?: string; href: string};
  jobs: {
    setShowJobs: React.Dispatch<React.SetStateAction<boolean>> | null;
    uploadType: allUploadType;
  };
  deleteButton: {
    selectedRowKeys: string[];
    handleDelete: (_id: string[] | string) => void;
  };
  shouldEdit?: boolean;
  refresh: {setRefresh: React.Dispatch<React.SetStateAction<boolean>>};
  isTable?: boolean;
  onlyYear?: boolean;
  showAddOnly?: boolean;
  onlyUpload?: boolean;
  shouldDisable?: boolean;
  tooltip?: string;
  blockAddition?: boolean;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  allSelectableCount?: number;
  onChangeStatus?: () => void;
  onBulkDelete?: () => void;
  hideIsDelete?: boolean;
  showUserCreationUploadConfirm?: boolean;
  // accessModuleType?: accessModule | undefined | null | string;
  // accessModuleDataLimit?: number;
}> = ({
  title = '',
  filter,
  addButton,
  sampleDownload,
  jobs,
  deleteButton,
  refresh,
  isTable = true,
  showAddOnly = false,
  onlyYear = false,
  onlyUpload = false,
  shouldDisable = true,
  tooltip = '',
  shouldEdit = true,
  blockAddition = false,
  onSelectAll,
  onDeselectAll,
  allSelectableCount,
  onChangeStatus,
  onBulkDelete,
  hideIsDelete = false,
  showUserCreationUploadConfirm = false
  // accessModuleType = null
  // accessModuleDataLimit = 0
}) => {
  const {facilityId = '', selectedYear = ''} = filter;
  const {setShowModel, setIsEdit, buttonTitle = 'Add'} = addButton;
  const {downTitle = 'Download Sample', href} = sampleDownload;
  const {setShowJobs, uploadType} = jobs;
  const {selectedRowKeys, handleDelete} = deleteButton;
  const {setRefresh} = refresh;
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadConfirmOpen, setIsUploadConfirmOpen] = useState(false);
  const [shouldOpenUploadPicker, setShouldOpenUploadPicker] = useState(false);
  const uploadTriggerRef = useRef<HTMLSpanElement>(null);
  const selectedIds = (
    selectedRowKeys as Array<string | null | undefined>
  ).filter((k): k is string => Boolean(k));
  const selectedCount = selectedIds.length;
  // const [openDownload, setOpenDownload] = useState(false);

  const isUploadDisabled =
    isTable && (onlyYear ? !selectedYear : !(facilityId && selectedYear));

  const uploadTooltipTitle = isTable
    ? onlyYear
      ? !selectedYear
        ? 'Please select year'
        : ''
      : !(facilityId && selectedYear)
        ? 'Please select facility and year'
        : ''
    : '';

  useEffect(() => {
    if (shouldOpenUploadPicker && uploadTriggerRef.current) {
      uploadTriggerRef.current.click();
      setShouldOpenUploadPicker(false);
    }
  }, [shouldOpenUploadPicker]);

  const handleUploadRequest: NonNullable<UploadProps['customRequest']> = (
    option
  ) => {
    setIsUploading(true);
    const {file, onSuccess, onError} = option;
    uploadFile(
      file,
      uploadType,
      isTable ? facilityId : '',
      isTable ? selectedYear : ''
    )
      .then(() => {
        onSuccess?.('ok');
        if (setShowJobs !== null) {
          setShowJobs(true);
        }
      })
      .catch((err) => {
        onError?.(err as Error);
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const uploadButton = (
    <Button
      icon={<UploadOutlined />}
      disabled={isUploadDisabled}
      loading={isUploading}
      onClick={
        showUserCreationUploadConfirm
          ? () => {
              setIsUploadConfirmOpen(true);
            }
          : undefined
      }
    >
      {isUploading ? 'Uploading....' : 'Click to Upload'}
    </Button>
  );

  return (
    <Flex vertical gap={16} className="tableTitleHeaderWrap">
      <Flex gap={16} className="tableButtonFlex">
        {title ? (
          <Flex align="center" justify="center" gap="1rem">
            <div className="tableTitle">{title}</div>
            {tooltip && (
              <Tooltip title={tooltip} autoAdjustOverflow={true} arrow={true}>
                <ExclamationCircleOutlined className="icon" />
              </Tooltip>
            )}
          </Flex>
        ) : (
          <div></div>
        )}
        <Flex className="tableButton">
          <Tooltip
            title={
              isTable
                ? onlyYear
                  ? !selectedYear
                    ? 'Please select year'
                    : 'Refresh Table'
                  : !(facilityId && selectedYear)
                    ? 'Please select facility and year'
                    : 'Refresh Table'
                : 'Refresh Table'
            }
          >
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => {
                setRefresh(true);
              }}
              disabled={
                isTable
                  ? onlyYear
                    ? !selectedYear
                    : !(facilityId && selectedYear)
                  : false
              }
            />
          </Tooltip>
          {!showAddOnly && shouldEdit && (
            <>
              {blockAddition || shouldBlockFreeOrReadOnly() ? (
                <></>
              ) : (
                <Tooltip
                  title={
                    isTable
                      ? onlyYear
                        ? !selectedYear
                          ? 'Please select year'
                          : ''
                        : !(facilityId && selectedYear)
                          ? 'Please select facility and year'
                          : ''
                      : ''
                  }
                >
                  <Button
                    onClick={() => {
                      if (setShowJobs !== null) {
                        setShowJobs(true);
                      }
                    }}
                    className="jobs"
                    disabled={
                      isTable
                        ? onlyYear
                          ? !selectedYear
                          : !(facilityId && selectedYear)
                        : false
                    }
                    icon={<JobIcon />}
                  />
                </Tooltip>
              )}

              {shouldBlockFreeOrReadOnly() || blockAddition ? (
                <></>
              ) : (
                <>
                  {/* {accessModuleType === accessModule.externalStakeholders ||
                accessModuleType === accessModule.facility ? (
                  <>
                    <DownloadSampleModal
                      open={openDownload}
                      href={href}
                      onDownload={() => {
                        setOpenDownload(false);
                      }}
                      onCancel={() => {
                        setOpenDownload(false);
                      }}
                      maxRecords={accessModuleDataLimit}
                      accessModuleType={accessModuleType}
                    />
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => {
                        setOpenDownload(true);
                      }}
                    >
                      {downTitle}
                    </Button>
                  </>
                ) : ( */}
                  <Tooltip
                    title={
                      isTable
                        ? onlyYear
                          ? !selectedYear
                            ? 'Please select year'
                            : ''
                          : !(facilityId && selectedYear)
                            ? 'Please select facility and year'
                            : ''
                        : ''
                    }
                  >
                    <Button
                      icon={<DownloadOutlined />}
                      href={
                        isTable
                          ? onlyYear
                            ? selectedYear
                              ? href
                              : undefined
                            : facilityId && selectedYear
                              ? href
                              : undefined
                          : href
                      }
                      download
                      disabled={
                        isTable
                          ? onlyYear
                            ? !selectedYear
                            : !(facilityId && selectedYear)
                          : false
                      }
                    >
                      {downTitle}
                    </Button>
                  </Tooltip>
                  {/* )} */}

                  <Tooltip title={uploadTooltipTitle}>
                    <span>
                      {showUserCreationUploadConfirm ? (
                        <>
                          <Upload
                            accept={SPREADSHEET_UPLOAD_ACCEPT}
                            customRequest={handleUploadRequest}
                            showUploadList={false}
                            beforeUpload={validateSpreadsheetFile}
                          >
                            <span
                              ref={uploadTriggerRef}
                              style={{display: 'none'}}
                            />
                          </Upload>
                          {uploadButton}
                          <Modal
                            title={USER_CREATION_UPLOAD_CONFIRM_TITLE}
                            open={isUploadConfirmOpen}
                            centered
                            okText="Continue"
                            cancelText="Close"
                            destroyOnClose
                            onOk={() => {
                              setIsUploadConfirmOpen(false);
                              setShouldOpenUploadPicker(true);
                            }}
                            onCancel={() => {
                              setIsUploadConfirmOpen(false);
                            }}
                          >
                            {getUserCreationUploadConfirmMessage()}
                          </Modal>
                        </>
                      ) : (
                        <Upload
                          accept={SPREADSHEET_UPLOAD_ACCEPT}
                          customRequest={handleUploadRequest}
                          showUploadList={false}
                          beforeUpload={validateSpreadsheetFile}
                        >
                          {uploadButton}
                        </Upload>
                      )}
                    </span>
                  </Tooltip>
                </>
              )}
            </>
          )}

          {!onlyUpload && shouldEdit && (
            <Tooltip
              title={
                isTable
                  ? onlyYear
                    ? !selectedYear
                      ? 'Please select year'
                      : ''
                    : !(facilityId && selectedYear)
                      ? 'Please select facility and year'
                      : ''
                  : ''
              }
            >
              {blockAddition || shouldBlockReadOnly() ? (
                <UpgradeButton title={buttonTitle} />
              ) : (
                <Button
                  onClick={() => {
                    if (isOnboardingDone() && setIsEdit && setShowModel) {
                      setShowModel(true);
                      setIsEdit(false);
                    }
                  }}
                  type="primary"
                  disabled={
                    isTable
                      ? onlyYear
                        ? !selectedYear && shouldDisable
                        : !(facilityId && selectedYear && shouldDisable)
                      : false && shouldDisable
                  }
                >
                  {buttonTitle}
                </Button>
              )}
            </Tooltip>
          )}
        </Flex>
      </Flex>
      {selectedCount > 0 && !shouldBlockReadOnly() && (
        <TableBulkSelectionBar
          selectedCount={selectedCount}
          selectedIds={selectedIds}
          handleDelete={handleDelete}
          onSelectAll={onSelectAll}
          onDeselectAll={onDeselectAll}
          allSelectableCount={allSelectableCount}
          onChangeStatus={onChangeStatus}
          onBulkDelete={onBulkDelete}
          hideIsDelete={hideIsDelete}
        />
      )}
    </Flex>
  );
};

export default TableTitleHeader;
